import { useQueries, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { getSlideById, getSlideByIdKeys, getSlidesViewerNavigationPrefetchData } from 'api/slides';
import { getHeatmapUrlKeyFromFeatureMetadata } from 'components/Procedure/Infobar/SlideInfobar/Results/Heatmaps/helpers';
import { getPublishedPrimaryRunOrchestrationId } from 'components/Procedure/Infobar/SlideInfobar/Results/helpers';
import { ParquetLayerType } from 'components/Procedure/useSlideChannelsAndResults/featureMetadata';
import { parseSlideExperimentResults } from 'components/Procedure/useSlideChannelsAndResults/parseSlideExperimentResults';
import { compact, find, uniq } from 'es-toolkit/compat';
import { SectionTable, TableColumn } from 'interfaces/agentSession';
import { FlowClassName, ResultsMode } from 'interfaces/experimentResults';
import {
  JobResultDimensionMeta as QaResultDimensionMeta,
  JobResultMergeTagsDefinition as QaResultMergeTagsDefinition,
  JobResultSourceConfig as QaResultSourceConfig,
} from 'interfaces/jobResultMetadata';
import { Slide, SlidePrefetchData } from 'interfaces/slide';
import { getIsMultiStain, MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import React from 'react';
import { ArrayParam, BooleanParam, JsonParam, StringParam } from 'use-query-params';
import { useFormatBracketsOptions } from 'utils/formatBrackets/formatBracketsOptions';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { encodeQueryParamsUsingSchema } from 'utils/useEncodedFilters/helpers';
import { useNavigationToViewerPage } from 'utils/useNavigationToViewerPage';

import {
  buildStudyReportViewerUrl,
  encodeStudyReportViewerLayerSettings,
  encodeStudyReportViewerMarkerChannelToggle,
  StudyReportViewerLayerSettingEntry,
  StudyReportViewerLinkResolver,
} from './studyReportViewerLinks';

const SLIDES_PREFETCH_QUERY_STALE_TIME_MS = 5 * 60 * 1000;

const slidesViewerNavigationPrefetchSchema = {
  filters: JsonParam,
  labId: StringParam,
  slideIdsToInclude: ArrayParam,
  skipPagination: BooleanParam,
};

const SLIDE_FIELD_NAME_PATTERN = /^slideids?(?:\d+)?$/;
const MARKER_FIELD_NAMES = new Set(['marker', 'markerid']);
const CELL_TYPE_FIELD_NAMES = new Set(['celltype', 'celltypes', 'celltypeid']);

const FIELD_NAME_NORMALIZATION_PATTERN = /[\s_-]+/g;

const normalizeFieldName = (value?: string | null) =>
  (value || '').replace(FIELD_NAME_NORMALIZATION_PATTERN, '').toLowerCase();

const isSlideFieldName = (value?: string | null) => SLIDE_FIELD_NAME_PATTERN.test(normalizeFieldName(value));

const stringifyEntityValue = (value: unknown) => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return '';
};

const getMergeTagEntityType = (mergeTags?: QaResultMergeTagsDefinition) => {
  for (const mergeTagValue of Object.values(mergeTags || {})) {
    if (typeof mergeTagValue !== 'string') {
      continue;
    }

    const matchedEntityType = mergeTagValue.match(/\{\{\s*(slide|marker|cellType)(?:\.[^:]+)?\s*:/)?.[1];
    if (matchedEntityType) {
      return matchedEntityType;
    }
  }

  return undefined;
};

const getEffectiveEntityType = (meta?: QaResultDimensionMeta | null) =>
  meta?.entityType || getMergeTagEntityType(meta?.mergeTags) || getMergeTagEntityType(meta?.itemMergeTags);

const getSerializedSeparator = (meta?: QaResultDimensionMeta | null) => meta?.separator || ', ';

const splitSerializedEntityValues = (value: unknown, separator: string) => {
  if (typeof value === 'string') {
    return compact(value.split(separator).map((item) => item.trim()));
  }

  if (Array.isArray(value)) {
    return compact(value.map((item) => stringifyEntityValue(item).trim()));
  }

  return compact([stringifyEntityValue(value).trim()]);
};

const resolveColumnMeta = ({ table, column }: { table: SectionTable; column: TableColumn }) => {
  if (column.meta) {
    return column.meta;
  }

  const dimensions = ((table.sourceConfig as QaResultSourceConfig | undefined)?.dimensions || []).filter(
    Boolean
  ) as QaResultDimensionMeta[];

  return find(dimensions, (dimension) => {
    const valueFields = Array.isArray(dimension.valueField)
      ? compact(dimension.valueField)
      : compact([dimension.valueField]);

    return dimension.key === column.field || valueFields.includes(column.field);
  });
};

const resolveColumnEntityType = ({ table, column }: { table: SectionTable; column: TableColumn }) => {
  const meta = resolveColumnMeta({ table, column });
  const entityType = getEffectiveEntityType(meta);

  if (meta?.serializedAs === 'comma_separated_string' || meta?.itemMergeTags) {
    return entityType || getMergeTagEntityType(meta.itemMergeTags);
  }

  if (entityType) {
    return entityType;
  }

  const normalizedField = normalizeFieldName(column.field);
  if (isSlideFieldName(normalizedField)) {
    return 'slide';
  }

  if (MARKER_FIELD_NAMES.has(normalizedField)) {
    return 'marker';
  }

  if (CELL_TYPE_FIELD_NAMES.has(normalizedField)) {
    return 'cellType';
  }

  return undefined;
};

const shouldSplitEntityValues = (meta?: QaResultDimensionMeta | null) =>
  meta?.serializedAs === 'comma_separated_string' || Boolean(meta?.itemMergeTags);

const getEntityValues = ({ rawValue, meta }: { rawValue: unknown; meta?: QaResultDimensionMeta | null }) => {
  if (shouldSplitEntityValues(meta) || Array.isArray(rawValue)) {
    return splitSerializedEntityValues(rawValue, getSerializedSeparator(meta));
  }

  return compact([stringifyEntityValue(rawValue).trim()]);
};

export interface StudyReportTableRowViewerContext {
  slideId?: string;
  slideIdsByField: Record<string, string[]>;
  slideFields: string[];
  markerIds: string[];
  cellTypeIds: string[];
}

export const extractStudyReportRowViewerContexts = ({
  table,
  rows,
}: {
  table: SectionTable;
  rows: Array<Record<string, unknown>>;
}) => {
  const columnsWithEntityTypes = table.columns.map((column) => {
    const meta = resolveColumnMeta({ table, column });

    return {
      column,
      meta,
      entityType: resolveColumnEntityType({ table, column }),
    };
  });

  return rows.reduce<Record<string, StudyReportTableRowViewerContext>>((contextsByRowId, row) => {
    let slideId: string | undefined;
    const slideIdsByField: Record<string, string[]> = {};
    const slideFields: string[] = [];
    const markerIds: string[] = [];
    const cellTypeIds: string[] = [];

    columnsWithEntityTypes.forEach(({ column, meta, entityType }) => {
      if (!entityType) {
        return;
      }

      const rawValue = row[column.field];
      const values = getEntityValues({ rawValue, meta });

      if (!values.length) {
        return;
      }

      if (entityType === 'slide') {
        slideIdsByField[column.field] = values;
        slideId = slideId || values[0];
        slideFields.push(column.field);
      }

      if (entityType === 'marker') {
        markerIds.push(...values);
      }

      if (entityType === 'cellType') {
        cellTypeIds.push(...values);
      }
    });

    contextsByRowId[String(row.id)] = {
      slideId,
      slideIdsByField,
      slideFields: uniq(slideFields),
      markerIds: uniq(compact(markerIds)),
      cellTypeIds: uniq(compact(cellTypeIds)),
    };

    return contextsByRowId;
  }, {});
};

const collectCachedSlidePrefetchData = ({
  queryClient,
  slideIds,
}: {
  queryClient: ReturnType<typeof useQueryClient>;
  slideIds: string[];
}) => {
  const slideIdsSet = new Set(slideIds);
  const cachedSlidesById: Record<string, SlidePrefetchData> = {};

  const cachedQueryPrefixes = [
    ['slidesViewerNavigationPrefetchData'],
    ['jobResultSlidesViewerNavigationPrefetchData'],
    ['studyReportSlidesViewerNavigationPrefetchData'],
  ];

  cachedQueryPrefixes.forEach((queryKey) => {
    for (const [, cachedSlideBatch] of queryClient.getQueriesData<SlidePrefetchData[]>({ queryKey })) {
      if (!Array.isArray(cachedSlideBatch)) {
        continue;
      }

      for (const cachedSlide of cachedSlideBatch) {
        if (slideIdsSet.has(cachedSlide.slideId) && !cachedSlidesById[cachedSlide.slideId]) {
          cachedSlidesById[cachedSlide.slideId] = cachedSlide;
        }
      }
    }
  });

  return cachedSlidesById;
};

const getVisibleMarkerIdsForSlide = ({
  slideViewerLinkDetails,
  markerIds,
}: {
  slideViewerLinkDetails?: SlidePrefetchData;
  markerIds: string[];
}) => {
  const slideMarkerIds = compact(Object.values(slideViewerLinkDetails?.channelMarkerTypes || {}));
  const toggleMarkerIds = uniq(compact([...slideMarkerIds, ...markerIds]));

  return {
    visibleMarkerIds: uniq(compact(markerIds)),
    toggleMarkerIds,
  };
};

const getNestedHeatmapOptionKey = ({ parentKey, nestedKey }: { parentKey?: string; nestedKey?: string }) => {
  if (!parentKey || !nestedKey) {
    return nestedKey || '';
  }

  const nestedKeyPrefix = `${parentKey}-`;
  return nestedKey.startsWith(nestedKeyPrefix) ? nestedKey.slice(nestedKeyPrefix.length) : nestedKey;
};

const buildSelectedCellTypeLayerSettings = ({
  slide,
  studyId,
  stainTypeId,
  cellTypeIds,
  formatBracketsOptions,
}: {
  slide: Slide;
  studyId?: string;
  stainTypeId: string;
  cellTypeIds: string[];
  formatBracketsOptions: ReturnType<typeof useFormatBracketsOptions>;
}) => {
  if (!cellTypeIds.length || !slide.experimentResults?.length || formatBracketsOptions.isLoading) {
    return null;
  }

  const publishedPrimaryRunOrchestrationId = getPublishedPrimaryRunOrchestrationId({
    experimentResults: slide.experimentResults,
    slideId: slide.id,
    studyId: slide.studyId || studyId || '',
  });

  const parsedResults = parseSlideExperimentResults(
    slide.experimentResults,
    formatBracketsOptions,
    getIsMultiStain(slide.stainingType || stainTypeId),
    false
  );
  const targetCellTypeIds = new Set(cellTypeIds);
  const layerSettings: StudyReportViewerLayerSettingEntry[] = [];

  parsedResults.heatmaps.publishedResults.forEach((heatmap) => {
    if (heatmap.parquetLayerType !== ParquetLayerType.CellClassification) {
      return;
    }

    if (
      heatmap.flowClassName === FlowClassName.CalculateFeatures &&
      publishedPrimaryRunOrchestrationId &&
      heatmap.orchestrationId !== publishedPrimaryRunOrchestrationId
    ) {
      return;
    }

    const matchingNestedHeatmaps = (heatmap.nestedItems || []).filter((nestedHeatmap) =>
      targetCellTypeIds.has(getNestedHeatmapOptionKey({ parentKey: heatmap.key, nestedKey: nestedHeatmap.key }))
    );

    if (!matchingNestedHeatmaps.length) {
      return;
    }

    layerSettings.push({
      layerUrlKey: getHeatmapUrlKeyFromFeatureMetadata({ heatmap }),
      settings: { selected: false, show: false },
    });

    (heatmap.nestedItems || []).forEach((nestedHeatmap) => {
      const nestedOptionKey = getNestedHeatmapOptionKey({ parentKey: heatmap.key, nestedKey: nestedHeatmap.key });
      const selected = targetCellTypeIds.has(nestedOptionKey);

      layerSettings.push({
        layerUrlKey: getHeatmapUrlKeyFromFeatureMetadata({ heatmap: nestedHeatmap, parentHeatmap: heatmap }),
        settings: { selected, show: selected },
      });
    });
  });

  return encodeStudyReportViewerLayerSettings({
    stainTypeId,
    layerSettings,
    viewerIndex: 0,
  });
};

export const useStudyReportTableViewerLinks = ({
  table,
  rows,
  studyId,
}: {
  table: SectionTable;
  rows: Array<Record<string, unknown>>;
  studyId?: string;
}): StudyReportViewerLinkResolver | undefined => {
  const queryClient = useQueryClient();
  const { labId } = useCurrentLabId();
  const { queryParams, generateEncodedParams } = useEncodedFilters();
  const { getUrlToCasePage, getUrlToSlidePage } = useNavigationToViewerPage();
  const formatBracketsOptions = useFormatBracketsOptions({
    addStain: false,
    studyId,
  });

  const rowContextsByRowId = React.useMemo(() => extractStudyReportRowViewerContexts({ table, rows }), [rows, table]);
  const slideIdsInScope = React.useMemo(
    () =>
      uniq(
        compact(Object.values(rowContextsByRowId).flatMap((context) => Object.values(context.slideIdsByField).flat()))
      ),
    [rowContextsByRowId]
  );
  const slideIdsRequiringCellTypeMetadata = React.useMemo(
    () =>
      uniq(
        compact(
          Object.values(rowContextsByRowId)
            .filter((context) => context.cellTypeIds.length > 0)
            .flatMap((context) => Object.values(context.slideIdsByField).flat())
        )
      ),
    [rowContextsByRowId]
  );

  const cachedSlidesById = React.useMemo(
    () => collectCachedSlidePrefetchData({ queryClient, slideIds: slideIdsInScope }),
    [queryClient, slideIdsInScope]
  );
  const missingSlideIds = React.useMemo(
    () => slideIdsInScope.filter((slideId) => !cachedSlidesById[slideId]),
    [cachedSlidesById, slideIdsInScope]
  );
  const slidePrefetchQueryParams = React.useMemo(() => {
    if (!studyId || !labId || !missingSlideIds.length) {
      return null;
    }

    return encodeQueryParamsUsingSchema(
      {
        filters: { studyId },
        labId,
        slideIdsToInclude: missingSlideIds,
        skipPagination: true,
      },
      slidesViewerNavigationPrefetchSchema,
      { arrayFormat: 'repeat' }
    );
  }, [labId, missingSlideIds, studyId]);
  const { data: queriedSlides = [] } = useQuery({
    queryKey: ['studyReportSlidesViewerNavigationPrefetchData', slidePrefetchQueryParams],
    queryFn: ({ signal }) => getSlidesViewerNavigationPrefetchData(slidePrefetchQueryParams || '', signal),
    enabled: Boolean(slidePrefetchQueryParams),
    staleTime: SLIDES_PREFETCH_QUERY_STALE_TIME_MS,
  });

  const slidesById = React.useMemo(
    () =>
      [...Object.values(cachedSlidesById), ...queriedSlides].reduce<Record<string, SlidePrefetchData>>(
        (currentSlidesById, slide) => {
          currentSlidesById[slide.slideId] = slide;
          return currentSlidesById;
        },
        {}
      ),
    [cachedSlidesById, queriedSlides]
  );

  const slideQueryEncodedFilters = React.useMemo(() => {
    if (!studyId) {
      return null;
    }

    return generateEncodedParams({
      filters: {
        ...(queryParams.filters || {}),
        studyId,
      },
      resultsMode: queryParams.resultsMode || ResultsMode.Published,
      page: 1,
      skipPagination: true,
    });
  }, [generateEncodedParams, queryParams.filters, queryParams.resultsMode, studyId]);

  const slideQueries = useQueries({
    queries: slideIdsRequiringCellTypeMetadata.map((slideId) => ({
      queryKey: getSlideByIdKeys({ slideId, encodedFilters: slideQueryEncodedFilters || '' }),
      queryFn: (context: { signal?: AbortSignal }) =>
        getSlideById({
          slideId,
          encodedFilters: slideQueryEncodedFilters || '',
          signal: context.signal,
        }),
      enabled: Boolean(slideQueryEncodedFilters) && !formatBracketsOptions.isLoading,
      staleTime: SLIDES_PREFETCH_QUERY_STALE_TIME_MS,
      retry: false,
    })),
  }) as UseQueryResult<Slide>[];

  const slideDetailsById = React.useMemo(
    () =>
      slideIdsRequiringCellTypeMetadata.reduce<Record<string, Slide | undefined>>(
        (currentSlideDetailsById, slideId, index) => {
          currentSlideDetailsById[slideId] = slideQueries[index]?.data;
          return currentSlideDetailsById;
        },
        {} as Record<string, Slide | undefined>
      ),
    [slideIdsRequiringCellTypeMetadata, slideQueries]
  );
  const slideQueryStateById = React.useMemo(
    () =>
      slideIdsRequiringCellTypeMetadata.reduce<Record<string, { isLoading: boolean; isError: boolean }>>(
        (currentSlideStateById, slideId, index) => {
          currentSlideStateById[slideId] = {
            isLoading: Boolean(slideQueries[index]?.isLoading),
            isError: Boolean(slideQueries[index]?.isError),
          };
          return currentSlideStateById;
        },
        {}
      ),
    [slideIdsRequiringCellTypeMetadata, slideQueries]
  );

  return React.useMemo<StudyReportViewerLinkResolver | undefined>(() => {
    if (!studyId) {
      return undefined;
    }

    return {
      resolveCellLink: ({ column: _column, field, row }) => {
        const rowContext = rowContextsByRowId[String(row.id)];
        const isSlideField = rowContext?.slideFields.includes(field);
        const targetSlideId = rowContext?.slideIdsByField[field]?.[0] || rowContext?.slideId;

        if (!targetSlideId || !isSlideField) {
          return null;
        }

        const slideViewerLinkDetails = slidesById[targetSlideId];
        const baseViewerUrl =
          typeof slideViewerLinkDetails?.caseId === 'number'
            ? getUrlToCasePage({
                caseId: slideViewerLinkDetails.caseId,
                caseStudyId: studyId,
                labId: slideViewerLinkDetails.labId || labId,
                selectedSlideIds: [targetSlideId],
                viewersStainPreferences: slideViewerLinkDetails.stainTypeId
                  ? [slideViewerLinkDetails.stainTypeId]
                  : undefined,
              })
            : getUrlToSlidePage({
                slideId: targetSlideId,
                caseStudyId: studyId,
                labId: slideViewerLinkDetails?.labId || labId,
              });

        const { visibleMarkerIds, toggleMarkerIds } = getVisibleMarkerIdsForSlide({
          slideViewerLinkDetails,
          markerIds: rowContext.markerIds,
        });
        const markerChannelToggle = encodeStudyReportViewerMarkerChannelToggle({
          visibleMarkerIds,
          toggleMarkerIds: toggleMarkerIds.length ? toggleMarkerIds : visibleMarkerIds,
          viewerIndex: 0,
        });

        let layerSettingsPerStainAndDisplayName: string | null = null;
        if (rowContext.cellTypeIds.length) {
          const slideDetails = slideDetailsById[targetSlideId];
          const slideQueryState = slideQueryStateById[targetSlideId];

          if (!slideDetails && slideQueryState?.isLoading) {
            return null;
          }

          if (slideDetails) {
            layerSettingsPerStainAndDisplayName = buildSelectedCellTypeLayerSettings({
              slide: slideDetails,
              studyId,
              stainTypeId:
                slideViewerLinkDetails?.stainTypeId ||
                ((table.sourceConfig as QaResultSourceConfig | undefined)?.stainTypeId as string | undefined) ||
                MULTIPLEX_STAIN_ID,
              cellTypeIds: rowContext.cellTypeIds,
              formatBracketsOptions,
            });
          }
        }

        return {
          href: buildStudyReportViewerUrl({
            baseViewerUrl,
            markerChannelToggle,
            layerSettingsPerStainAndDisplayName,
          }),
          actionLabel:
            typeof slideViewerLinkDetails?.caseId === 'number' ? 'Open case in viewer' : 'Open slide in viewer',
          previewSlide: {
            id: targetSlideId,
            labId: slideViewerLinkDetails?.labId || labId,
          },
        };
      },
    };
  }, [
    formatBracketsOptions,
    getUrlToCasePage,
    getUrlToSlidePage,
    labId,
    rowContextsByRowId,
    slideDetailsById,
    slideQueryStateById,
    slidesById,
    studyId,
    table.sourceConfig,
  ]);
};
